const BASE = import.meta.env.VITE_API_URL ?? "http://localhost:3000";

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem("sq_token");
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers ?? {}),
    },
  });

  const json = await res.json();
  if (!res.ok) throw Object.assign(new Error(json.error ?? "Request failed"), { code: json.code, status: res.status });
  return json.data as T;
}

const get  = <T>(path: string) => request<T>(path);
const post = <T>(path: string, body: unknown) => request<T>(path, { method: "POST",  body: JSON.stringify(body) });
const put  = <T>(path: string, body: unknown) => request<T>(path, { method: "PUT",   body: JSON.stringify(body) });
const patch= <T>(path: string, body: unknown) => request<T>(path, { method: "PATCH", body: JSON.stringify(body) });
const del  = <T>(path: string) => request<T>(path, { method: "DELETE" });

export const api = {
  // Auth
  sendOtp:      (email: string)               => post("/auth/otp/send",   { email }),
  verifyOtp:    (email: string, code: string) => post<{ token: string; needsSetup: boolean; familyId?: string }>("/auth/otp/verify", { email, code }),
  kidLogin:     (familyId: string, kidId: string, pin: string) =>
    post<{ token: string; kid: import("@starquest/core").Kid }>("/auth/kid-login", { familyId, kidId, pin }),
  familyLookup: (code: string) =>
    get<{ familyId: string; familyName: string; kids: Pick<import("@starquest/core").Kid, "kidId"|"name"|"avatar"|"nickname">[] }>(`/family-lookup/${code}`),
  getFamilyCode: (fid: string) =>
    get<{ familyCode: string }>(`/families/${fid}/code`),
  setFamilyCode: (fid: string, familyCode: string) =>
    put<{ familyCode: string }>(`/families/${fid}/code`, { familyCode }),
  resetKidPin: (fid: string, kidId: string, pin: string) =>
    patch(`/families/${fid}/kids/${kidId}`, { pin }),

  // Family
  createFamily:  (name: string, familyCode?: string) => post("/families", { name, ...(familyCode ? { familyCode } : {}) }),
  getFamily:     (fid: string)             => get(`/families/${fid}`),
  updateFamily:  (fid: string, body: unknown) => patch(`/families/${fid}`,               body),

  // Kids
  listKids:      (fid: string)             => get(`/families/${fid}/kids`),
  createKid:     (fid: string, body: unknown) => post(`/families/${fid}/kids`,            body),
  updateKid:     (fid: string, kid: string, body: unknown) => patch(`/families/${fid}/kids/${kid}`, body),
  deleteKid:     (fid: string, kid: string) => del(`/families/${fid}/kids/${kid}`),

  // Rooms
  listRooms:     (fid: string)             => get(`/families/${fid}/rooms`),
  saveRooms:     (fid: string, rooms: unknown[]) => put(`/families/${fid}/rooms`,         { rooms }),

  // Challenges
  listChallenges:   (fid: string)               => get(`/families/${fid}/challenges`),
  saveChallenge:    (fid: string, body: unknown) => post(`/families/${fid}/challenges`, body),
  deleteChallenge:  (fid: string, cid: string)  => del(`/families/${fid}/challenges/${cid}`),

  // Completions
  listCompletions: (fid: string, status?: string) =>
    get(`/families/${fid}/completions${status ? `?status=${status}` : ""}`),
  createCompletion: (fid: string, body: unknown) => post(`/families/${fid}/completions`, body),
  reviewCompletion: (fid: string, cid: string, action: "approve"|"reject", rejectionReason?: string) =>
    post(`/families/${fid}/completions/${cid}/review`, { action, rejectionReason }),

  // Cashouts
  listCashouts:   (fid: string)            => get(`/families/${fid}/cashouts`),
  requestCashout: (fid: string, body: unknown) => post(`/families/${fid}/cashouts`,       body),
  reviewCashout:  (fid: string, cid: string, action: "approve"|"decline") =>
    post(`/families/${fid}/cashouts/${cid}/review`, { action }),

  // Bonus
  sendBonus: (fid: string, body: unknown)  => post(`/families/${fid}/bonus`,              body),

  // Transactions
  listTransactions: (fid: string, kidId: string) => get(`/families/${fid}/transactions?kidId=${kidId}`),

  // Upload URL for quest photos
  getUploadUrl: (familyId: string, kidId: string, contentType: string) =>
    post<{ uploadUrl: string; photoKey: string }>("/upload-url", { familyId, kidId, contentType }),

  // Providers
  listProviders:  (fid: string) =>
    get<import("@starquest/core").Provider[]>(`/families/${fid}/providers`),
  saveProviders:  (fid: string, providers: unknown[]) =>
    put(`/families/${fid}/providers`, { providers }),
  deleteProvider: (fid: string, pid: string) =>
    del(`/families/${fid}/providers/${pid}`),

  // Items
  listItems:  (fid: string) =>
    get<import("@starquest/core").StoreItem[]>(`/families/${fid}/items`),
  saveItem:   (fid: string, iid: string, body: unknown) =>
    put<import("@starquest/core").StoreItem>(`/families/${fid}/items/${iid}`, body),
  deleteItem: (fid: string, iid: string) =>
    del(`/families/${fid}/items/${iid}`),

  // Wishes
  listWishes:   (fid: string) =>
    get<import("@starquest/core").Wish[]>(`/families/${fid}/wishes`),
  createWish:   (fid: string, body: { kidId: string; name: string; url?: string }) =>
    post<import("@starquest/core").Wish>(`/families/${fid}/wishes`, body),
  reviewWish:   (fid: string, wid: string, body: unknown) =>
    put(`/families/${fid}/wishes/${wid}`, body),

  // Redemptions
  listRedemptions:  (fid: string) =>
    get<import("@starquest/core").Redemption[]>(`/families/${fid}/redemptions`),
  createRedemption: (fid: string, body: { kidId: string; itemId: string }) =>
    post<import("@starquest/core").Redemption>(`/families/${fid}/redemptions`, body),
  reviewRedemption: (fid: string, rid: string, body: unknown) =>
    put(`/families/${fid}/redemptions/${rid}`, body),

  // Wishlists
  getWishlist:    (fid: string, kidId: string) =>
    get<{ itemIds: string[] }>(`/families/${fid}/wishlists/${kidId}`),
  toggleWishlist: (fid: string, kidId: string, iid: string) =>
    put<{ hearted: boolean }>(`/families/${fid}/wishlists/${kidId}/${iid}`, {}),

  // Tasks — Templates
  listTasks: (fid: string) =>
    get<import("@starquest/core").Task[]>(`/families/${fid}/tasks`),
  saveTask: (fid: string, body: { name: string; emoji: string; description?: string; baseReward: number; xp: number }) =>
    post<import("@starquest/core").Task>(`/families/${fid}/tasks`, body),
  deleteTask: (fid: string, taskId: string) =>
    del(`/families/${fid}/tasks/${taskId}`),

  // Tasks — Assignments
  listTaskAssignments: (fid: string, params?: { kidId?: string; status?: string }) =>
    get<import("@starquest/core").TaskAssignment[]>(
      `/families/${fid}/task-assignments${params && Object.keys(params).length ? `?${new URLSearchParams(params as Record<string, string>).toString()}` : ""}`
    ),
  createTaskAssignment: (fid: string, body: {
    kidId: string; taskId: string; taskName: string; taskEmoji: string;
    baseReward: number; xp: number; dueDate: string;
    recurrence: import("@starquest/core").TaskRecurrence | null;
  }) => post<import("@starquest/core").TaskAssignment>(`/families/${fid}/task-assignments`, body),
  submitTaskAssignment: (fid: string, aid: string) =>
    post<import("@starquest/core").TaskAssignment>(`/families/${fid}/task-assignments/${aid}/submit`, {}),
  reviewTaskAssignment: (fid: string, aid: string, action: "approve" | "reject", rejectionReason?: string) =>
    post<{ action: string; pointsAwarded?: number }>(`/families/${fid}/task-assignments/${aid}/review`, { action, rejectionReason }),
  deleteTaskAssignment: (fid: string, aid: string) =>
    del(`/families/${fid}/task-assignments/${aid}`),

  // Members
  listMembers:  (fid: string) =>
    get<import("@starquest/core").FamilyMember[]>(`/families/${fid}/members`),
  updateMember: (fid: string, memberId: string, body: unknown) =>
    memberId === "current-user"
      ? patch(`/families/${fid}/members/me`, body)
      : patch(`/families/${fid}/members/${memberId}`, body),
  updateMemberEmail: (fid: string, memberId: string, email: string) =>
    patch(`/families/${fid}/members/${memberId}`, { email }),
  requestEmailChange: (fid: string, memberId: string, newEmail: string) =>
    post(`/families/${fid}/members/${memberId}/request-email-change`, { newEmail }),
  verifyEmailChange: (fid: string, memberId: string, newEmail: string, code: string) =>
    post(`/families/${fid}/members/${memberId}/verify-email-change`, { newEmail, code }),
  deleteMember: (fid: string, memberId: string) =>
    del(`/families/${fid}/members/${memberId}`),

  // Invites
  createInvite: (fid: string, role: "manager" | "viewer" = "manager") =>
    post<{ inviteCode: string; inviteUrl: string; expiresAt: string }>(`/families/${fid}/invites`, { role }),
  listInvites:  (fid: string) =>
    get<import("@starquest/core").FamilyInvite[]>(`/families/${fid}/invites`),
  deleteInvite: (fid: string, inviteCode: string) =>
    del(`/families/${fid}/invites/${inviteCode}`),
  getInvite:    (inviteCode: string) =>
    get<{ valid: boolean; familyName?: string; expiresAt?: string; reason?: string }>(`/invites/${inviteCode}`),
  verifyOtpWithInvite: (email: string, code: string, inviteCode: string) =>
    post<{ token: string; needsSetup: boolean; familyId?: string }>(
      "/auth/otp/verify", { email, code, inviteCode }
    ),

  // Theme
  requestTheme: (fid: string, kidId: string, themeId: string) =>
    post<{ requested: boolean; themeId: string; requestedAt: string }>(
      `/families/${fid}/kids/${kidId}/theme-request`, { themeId }
    ),
  approveTheme: (fid: string, kidId: string) =>
    post<{ approved: boolean; theme: string }>(
      `/families/${fid}/kids/${kidId}/theme-request/approve`, {}
    ),
};
